import React from 'react';
import { motion } from 'framer-motion';
import { Target, Heart, Users, BookOpen, Compass, Crown } from 'lucide-react';
import AnimatedSection from './AnimatedSection';

const About: React.FC = () => {
  const visionPoints = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "World-wide Impact",
      description: "Expanding globally to transform the world's spiritual landscape"
    },
    {
      icon: <Compass className="w-6 h-6" />,
      title: "Replicable Process",
      description: "Developing curriculum and systems that can be implemented anywhere"
    },
    {
      icon: <Crown className="w-6 h-6" />,
      title: "Spiritual Legacy",
      description: "Leaving a lasting impact for future generations through faithful men"
    }
  ];

  const missionPoints = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Weekly Gatherings",
      description: "Men coming together every Monday for fellowship and growth"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Discipleship",
      description: "Deep relationships in Christ, going wide through authentic community"
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Word-Centered",
      description: "Studying Scripture accurately and humbly, seeking Truth and Wisdom"
    }
  ];

  const coreValues = [
    "Acceptance and Accountability",
    "Humble Leadership - We have facilitators, not pastors",
    "\"We're just dudes who love Jesus\"",
    "Doing Life Together",
    "Reconciliation with God and others",
    "Iron Sharpens Iron",
    "Going deeper than Sunday mornings"
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <AnimatedSection className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">About Us</h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
        </AnimatedSection>
        
        <div className="max-w-6xl mx-auto space-y-20">
          {/* Vision Statement */}
          <AnimatedSection delay={0.2}>
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Our Vision</h3>
              <div className="bg-blue-600 text-white p-8 rounded-xl shadow-lg max-w-4xl mx-auto">
                <p className="text-xl md:text-2xl font-medium leading-relaxed">
                  "We envision a world renewed by Christ through men who are leading with faith, 
                  shaping culture, and leaving a legacy for the glory of God"
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {visionPoints.map((point, index) => (
                <motion.div 
                  key={point.title}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.2 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="text-blue-600 mb-4">{point.icon}</div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-3">{point.title}</h4>
                  <p className="text-gray-600">{point.description}</p>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>

          {/* Mission Statement */}
          <AnimatedSection delay={0.6}>
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Our Mission</h3>
              <div className="bg-gray-800 text-white p-8 rounded-xl shadow-lg max-w-4xl mx-auto">
                <p className="text-xl md:text-2xl font-medium leading-relaxed">
                  "We exist to disciple men in Christ through weekly gatherings, doing life together, and a
                  reverent study of Scripture"
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {missionPoints.map((point, index) => (
                <motion.div 
                  key={point.title}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.2 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="text-blue-600 mb-4">{point.icon}</div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-3">{point.title}</h4>
                  <p className="text-gray-600">{point.description}</p>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>

          {/* Core Values */}
          <AnimatedSection delay={1.0}>
            <div className="bg-blue-50 rounded-xl p-8 md:p-12">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8 text-center">How We Live It Out</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {coreValues.map((value, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start space-x-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.2 + index * 0.1 }}
                  >
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <p className="text-gray-700 text-lg">{value}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* Call to Action */}
          <AnimatedSection delay={1.4} className="text-center">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl mx-auto">
              <h4 className="text-xl font-semibold text-gray-800 mb-4">Growing Leaders Together</h4>
              <p className="text-gray-600 mb-6">
                We believe in the power of men coming together to sharpen one another, 
                grow in wisdom, and become the leaders God has called us to be.
              </p>
              <motion.a 
                href="#contact" 
                className="inline-flex items-center text-blue-600 font-medium hover:text-blue-800 transition-colors duration-300"
                whileHover={{ x: 10 }}
              >
                Join our next meeting
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </motion.a>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default About;